import React from 'react';

import { parseNullableValue } from '@clearverify/utils';

import { EmployersTableListDocument, useSendInvitesToEmployersByPeoPpMutation } from 'src/api';
import { EMPLOYERS_UPLOAD_BY_PEO_PP_TEMPLATE } from 'src/assets/employer';
import { BulkUploadForm, BulkUploadTable } from 'src/components/bulkUpload';
import type { TableColumn } from 'src/components/table';
import { EMPLOYERS_UPLOAD_BY_PEO_PP_TEMPLATE_NAME } from 'src/constants/templates';
import { useNotification } from 'src/hooks/app';
import { useEmployersUnderPEOPayrollProviderQueryVariables } from 'src/hooks/employer';
import { useCurrentPeoPayrollProviderStaff } from 'src/hooks/peoPayrollProviderStaff';
import { TableDataProvider } from 'src/providers/TableDataProvider';
import { clearEntitiesData } from 'src/utils/bulkUpload';

import { StepId, templateSchema } from './constants';
import { ExtendedInvitedEmployerData, InvitedEmployerData } from './types';
import { convertDataWithSchema } from './utils/convertDataWithSchema';
import { validateEmployers } from './utils/validateEmployers';

interface InviteEmployersByPeoPpModalContentProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
  peoPayrollProviderId?: string;
}

export const InviteEmployersByPeoPpModalContent: React.FC<
  InviteEmployersByPeoPpModalContentProps
> = ({ closeModal, switchClosability, peoPayrollProviderId: externalPeoPayrollProviderId }) => {
  const [currentStep, setCurrentStep] = React.useState<StepId>(StepId.Upload);
  const [employers, setEmployers] = React.useState<ExtendedInvitedEmployerData[]>([]);

  const { peoPayrollProviderId: peoPayrollProviderIdFromUser } =
    useCurrentPeoPayrollProviderStaff();

  const queryVariables = useEmployersUnderPEOPayrollProviderQueryVariables(
    externalPeoPayrollProviderId ?? peoPayrollProviderIdFromUser,
  );

  const { createApolloContext } = useNotification();

  const [sendInvites, { loading: sendInvitesLoading }] = useSendInvitesToEmployersByPeoPpMutation({
    context: createApolloContext({
      errorMessage: 'Failed to invite employers',
      successMessage: 'Employers have been successfully invited',
    }),
    refetchQueries: [{ query: EmployersTableListDocument, variables: queryVariables }],
  });

  const handleEmployersData = React.useCallback((employers: InvitedEmployerData[]) => {
    const validatedEmployers = validateEmployers(employers);
    setEmployers(validatedEmployers);
    setCurrentStep(StepId.Review);
  }, []);

  const handleSaveChanges = React.useCallback(async () => {
    try {
      switchClosability(false);

      const validEmployers = employers.filter(employer => employer.valid);

      if (validEmployers.length > 0) {
        await sendInvites({
          variables: {
            input: {
              employers: clearEntitiesData(validEmployers),
              peoPayrollProviderId: externalPeoPayrollProviderId,
            },
          },
        });
      }

      switchClosability(true);
      closeModal();
    } catch {
      switchClosability(true);
    }
  }, [closeModal, employers, externalPeoPayrollProviderId, sendInvites, switchClosability]);

  const handleCancel = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleBack = React.useCallback(() => {
    setEmployers([]);
    setCurrentStep(StepId.Upload);
  }, []);

  const loading = sendInvitesLoading;

  if (currentStep === StepId.Upload) {
    return (
      <BulkUploadForm
        convertDataWithSchema={convertDataWithSchema}
        templateSchema={templateSchema}
        templateDownloadLink={EMPLOYERS_UPLOAD_BY_PEO_PP_TEMPLATE}
        templateDownloadName={EMPLOYERS_UPLOAD_BY_PEO_PP_TEMPLATE_NAME}
        topNotice={uploadFormTopNotice}
        handleEntitiesData={handleEmployersData}
        handleCancel={handleCancel}
        switchClosability={switchClosability}
        nextStepLoading={loading}
      />
    );
  }

  return (
    <TableDataProvider>
      <BulkUploadTable
        entitites={employers}
        columns={tableColumns}
        invalidEntititesMessage={invalidEntititesMessage}
        handleSaveChanges={handleSaveChanges}
        handleBack={handleBack}
        loading={loading}
      />
    </TableDataProvider>
  );
};

const uploadFormTopNotice =
  "Using this modal, you can upload the file with Employers from several different PEOs. You just need to specify the PEO's FEIN for each Employer in the file. If an Employer is your direct client, do not specify the PEO's FEIN. Please note that the Employers you add must be then set to Live status.";

const invalidEntititesMessage =
  'There were some errors while processing the file provided. Some fields are either missing or have been inaccurately formatted. If you proceed with this upload, the Employers highlighted red will not be added.';

const tableColumns: TableColumn<ExtendedInvitedEmployerData>[] = [
  {
    key: 'peoFein',
    title: 'PEO FEIN',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.peoFein);
    },
  },
  {
    key: 'employerFein',
    title: 'Employer FEIN',
    render: (_valueByDataPath, employer) => {
      return employer.employerFein;
    },
  },
  {
    key: 'feinRegisteredBusinessName',
    title: 'Registered Business Name',
    render: (_valueByDataPath, employer) => {
      return employer.feinRegisteredBusinessName;
    },
  },
  {
    key: 'feinCommercialName',
    title: 'Commercial Name',
    render: (_valueByDataPath, employer) => {
      return employer.feinCommercialName;
    },
  },
  {
    key: 'displayName',
    title: 'Display Name',
    render: (_valueByDataPath, employer) => {
      return employer.displayName;
    },
  },
  {
    key: 'industry',
    title: 'Industry',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.industry);
    },
  },
  {
    key: 'corporateStructure',
    title: 'Corporate Structure',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.corporateStructure);
    },
  },
  {
    key: 'taxYearEndDate',
    title: 'Tax Year End Date',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.taxYearEndDate);
    },
  },
  {
    key: 'numberOfEmployees',
    title: 'Number of Employees',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.numberOfEmployees);
    },
  },
  {
    key: 'hqAddress',
    title: 'HQ Address',
    render: (_valueByDataPath, employer) => {
      return employer.hqAddress;
    },
  },
  {
    key: 'hqCity',
    title: 'HQ City',
    render: (_valueByDataPath, employer) => {
      return employer.hqCity;
    },
  },
  {
    key: 'hqCounty',
    title: 'HQ County',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.hqCounty);
    },
  },
  {
    key: 'hqState',
    title: 'HQ State',
    render: (_valueByDataPath, employer) => {
      return employer.hqState;
    },
  },
  {
    key: 'hqZipCode',
    title: 'HQ ZIP Code',
    render: (_valueByDataPath, employer) => {
      return employer.hqZipCode;
    },
  },
  {
    key: 'contactEmail',
    title: 'Contact Email',
    render: (_valueByDataPath, employer) => {
      return employer.contactEmail;
    },
  },
  {
    key: 'emailForInvitation',
    title: 'Email for Invitation',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.emailForInvitation);
    },
  },
  {
    key: 'contactPhone',
    title: 'Contact Phone',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(employer.contactPhone);
    },
  },
];
